import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import cloneDeep from "lodash/cloneDeep";
import { isEmpty } from "lodash";
import useBodyScrollability from "@/hooks/useBodyScrollability";
import { FILTER_QUERY_PARAMS } from "@/routes/productListing/constants";
import PlpAnalytics from "@/services/analytics/main/plp";
import { SORT_LOCATION } from "@/services/analytics/main/plp/types";
import { handleFilterEvent } from "@/routes/productListing/analyticsHelper";
import FilterSidebar from "./filtersSidebar";
import FiltersPanel from "@/routes/productListing/productListingFiltersMob/filtersPanel";
import { Facet, FilterOption, FlashSaleFiltersProps } from "../types";
import "@/routes/productListing/productListingFiltersMob/style.scss";
import { SalePageDataContext } from "../../SaleWrapper";
import { SaleService } from "@/services";
import { selectedFiltersContext as SelectedFiltersMobContext } from "@/routes/productListing/productListingFiltersMob";
import { SelectedFiltersType as PLPSelectedFiltersType } from "@/routes/productListing/types";
import { splitSizeFacetInFacets } from "@/routes/productListing/utils";

const FlashSaleFiltersMob: FC<FlashSaleFiltersProps> = ({ tabDetails }) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] =
    useState<PLPSelectedFiltersType>({});
  const selectedFiltersRef = useRef<PLPSelectedFiltersType>({});
  const openedWithFiltersSnapshotRef = useRef<PLPSelectedFiltersType | null>(
    null
  );
  const saleDataSnapshotRef = useRef<any>(null);
  const selectedFiltersQuerySnapshotRef = useRef<any>(null);

  const {
    pageData,
    saleData,
    setSaleData,
    setSelectedFiltersQuery,
    selectedFiltersQuery
  } = useContext(SalePageDataContext);

  useEffect(() => {
    if (saleData && isEmpty(saleData.selectedFilters)) {
      setSelectedFilters({});
      selectedFiltersRef.current = {};
      setSelectedFiltersQuery({});
    }
  }, [saleData]);

  const bannerData = pageData.data;
  const pageContentValue = bannerData.slug;

  const { enableBodyScroll, disableBodyScroll } = useBodyScrollability();

  useEffect(() => {
    if (!isFiltersOpen) {
      enableBodyScroll();
    }
  }, [isFiltersOpen]);

  const getUpdatedQuery = useCallback(
    (updatedSelection: PLPSelectedFiltersType) => {
      const queryObject = Object.values(updatedSelection).reduce(
        (acc, filter) => {
          acc[filter.queryParam] = filter.selectedOptions.map(
            option => option.key
          );
          return acc;
        },
        {}
      );

      return queryObject;
    },
    [selectedFilters]
  );

  const updateSelectedFilters = useCallback(
    selected => {
      let updated: PLPSelectedFiltersType = { ...selectedFiltersRef.current };
      if (selected) {
        if (Array.isArray(selected)) {
          selected.forEach(item => {
            updated[item.queryParam] = item;
          });
        } else {
          updated[selected.queryParam] = selected;
        }
      }

      setSelectedFilters(selected => ({ ...selected, ...updated }));
      selectedFiltersRef.current = {
        ...selectedFiltersRef.current,
        ...updated
      };

      return updated;
    },
    [selectedFilters]
  );

  const onFilterChange = useCallback(
    async (query?: {}) => {
      setSelectedFiltersQuery(query);
      const { data } = await SaleService.getSaleSpecificTabData(
        tabDetails.slug,
        pageContentValue,
        query
      );
      setSaleData(data);
    },
    [tabDetails]
  );

  const onSelection = useCallback(
    selected => {
      const updatedSelection = selected
        ? updateSelectedFilters(selected)
        : selectedFiltersRef.current;

      const query = getUpdatedQuery(updatedSelection);

      onFilterChange(query);

      if (selected?.queryParam === FILTER_QUERY_PARAMS.SORT) {
        PlpAnalytics.sort({
          sort_by: selected?.selectedOptions?.[0]?.defaultValue?.toLowerCase(),
          sort_location: SORT_LOCATION.PRODUCT_LIST
        });
      } else if (selected) {
        //@ts-ignore
        handleFilterEvent(selected, updatedSelection);
      }
    },
    [updateSelectedFilters, getUpdatedQuery, onFilterChange, selectedFilters]
  );

  const preparedFilters = useMemo(
    () => splitSizeFacetInFacets(saleData.facets ?? []) as Facet[],
    [saleData.facets]
  );

  if (!saleData.facets) return null;

  const onFiltersOpen = () => {
    openedWithFiltersSnapshotRef.current = cloneDeep(
      selectedFiltersRef.current
    );
    saleDataSnapshotRef.current = saleData;
    selectedFiltersQuerySnapshotRef.current = selectedFiltersQuery;
    setIsFiltersOpen(true);
    disableBodyScroll();
  };

  const onFiltersClose = (applied?: boolean) => {
    if (!applied) {
      const snapshot = openedWithFiltersSnapshotRef.current;
      if (snapshot !== null) {
        const snapshotClone = cloneDeep(snapshot);
        setSelectedFilters(snapshotClone);
        selectedFiltersRef.current = snapshotClone;
        setSaleData(saleDataSnapshotRef.current);
        setSelectedFiltersQuery(selectedFiltersQuerySnapshotRef.current ?? {});
      }
    }
    openedWithFiltersSnapshotRef.current = null;
    saleDataSnapshotRef.current = null;
    selectedFiltersQuerySnapshotRef.current = null;
    setIsFiltersOpen(false);
  };

  if (saleData && isEmpty(saleData?.facets) && isFiltersOpen) {
    onFiltersClose();
  }

  return (
    <div className="flash_sale_filters_wrapper_resp filters_wrapper_resp">
      <SelectedFiltersMobContext.Provider
        value={{
          onSelection,
          selectedFilters,
          setSelectedFilters,
          updateSelectedFilters,
          isFiltersOpen
        }}
      >
        <FiltersPanel
          itemsTotal={saleData.nbHits}
          filters={preparedFilters as any}
          onFiltersOpen={onFiltersOpen}
          appliedFilterCount={saleData.appliedFilterCount}
        />
        <FilterSidebar
          filters={preparedFilters}
          isFiltersOpen={isFiltersOpen}
          onFiltersClose={onFiltersClose}
          itemsTotal={saleData.nbHits}
        />
      </SelectedFiltersMobContext.Provider>
    </div>
  );
};

export default FlashSaleFiltersMob;
